import { useEffect, useState } from "react";
import { FileSignature, Download, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/Field";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BAD_DATE = "Choose a date within the next 30 days";
const todayET = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

async function download(loanId, gt, loanNumber) {
  const { data } = await api.get(`/loans/${loanId}/payoff-letter`, { params: { good_through: gt }, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${loanNumber}-payoff-letter-${gt}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const Quote = ({ q }) => (
  <div className="rounded-md border bg-muted/30 p-4 text-sm" data-testid="payoff-quote">
    {[["Outstanding principal", q.principal_cents], [`Interest through ${fmtDate(q.good_through)}`, q.interest_cents], ["Outstanding fees", q.fees_cents]].map(([k, v]) => (
      <div key={k} className="flex justify-between py-0.5"><span className="text-muted-foreground">{k}</span><span className="num">{money(v)}</span></div>
    ))}
    <div className="mt-2 flex justify-between border-t pt-2 font-semibold"><span>Total payoff</span><span className="num" data-testid="payoff-quote-total">{money(q.total_cents)}</span></div>
    <div className="mt-1 text-xs text-muted-foreground">Per-diem after that date: {money(q.per_diem_cents)} / day</div>
    {q.pending.map((t, i) => (
      <div key={i} className="mt-1 text-xs font-medium text-destructive" data-testid={`payoff-quote-pending-${i}`}>Pending payment — not yet credited: {money(t.amount_cents)}</div>
    ))}
  </div>
);

export const PayoffLetterButton = ({ loan, admin, onSent }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [gt, setGt] = useState("");
  const [err, setErr] = useState("");
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const today = todayET();
  const max = addDays(today, 30);

  useEffect(() => { if (open) { setGt(addDays(todayET(), 10)); setErr(""); } }, [open]);
  useEffect(() => {
    if (!open || !gt) return;
    setQ(null);
    if (gt < today || gt > max) { setErr(BAD_DATE); return; }
    setErr("");
    api.get(`/loans/${loan.id}/payoff-quote`, { params: { good_through: gt } }).then((r) => setQ(r.data)).catch((e) => setErr(apiError(e).message));
  }, [open, gt, loan.id, today, max]);

  const submit = async (e) => {
    e.preventDefault();
    if (!gt || gt < today || gt > max) { setErr(BAD_DATE); return; }
    setBusy(true);
    try {
      await download(loan.id, gt, loan.loan_number);
      toast.success(`Payoff letter good through ${fmtDate(gt)} downloaded`);
      setOpen(false);
    } catch (ex) {
      setErr(apiError(ex).message);
    } finally {
      setBusy(false);
    }
  };

  const emailIt = async () => {
    if (!gt || gt < today || gt > max) { setErr(BAD_DATE); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/loans/${loan.id}/payoff-letter/email`, { good_through: gt });
      (data.status === "Sent" ? toast.success : toast.error)(data.message, { id: "payoff-email-result" });
      onSent?.();
    } catch (ex) {
      toast.error(apiError(ex).message, { id: "payoff-email-result" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="payoff-letter-button"><FileSignature /> Payoff letter</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payoff-letter-dialog">
          <form onSubmit={submit} noValidate className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Payoff letter · {loan.loan_number}</DialogTitle>
              <DialogDescription>The exact amount needed to pay this loan in full by the date you choose (up to 30 days ahead).</DialogDescription>
            </DialogHeader>
            <Field label="Good through" name="good_through" error={err}>
              <Input id="good_through" type="date" value={gt} min={today} max={max} onChange={(e) => setGt(e.target.value)}
                className={err ? "border-destructive" : ""} data-testid="payoff-good-through-input" />
            </Field>
            {q && !err && <Quote q={q} />}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="payoff-cancel-button">Cancel</Button>
              {admin && (
                <Button type="button" variant="outline" onClick={emailIt} disabled={sending} data-testid="payoff-email-button">
                  {sending ? <Loader2 className="animate-spin" /> : <Mail />} Email to borrower
                </Button>
              )}
              <Button type="submit" disabled={busy} data-testid="payoff-download-button">{busy ? <Loader2 className="animate-spin" /> : <Download />} Download PDF</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
